import React from "react";
import { ArticleLayout } from "../components/ArticleLayout";

const IMG = "/aesthetics-hero.jpeg";

const FAQS = [
  { q: "How much larger should the bottom border be for a gallery mat?", a: "The classic 'weighted bottom' look makes the bottom border heavier than the top and sides - typically 1.2 to 1.5 times wider. This counteracts an optical illusion where an evenly-matted image appears to sit low in the frame. A weighting factor around 1.3 is a reliable starting point; Framer's Bench lets you dial it in and see the effect instantly." },
  { q: "What's the best proportion for frame, mat and artwork?", a: "Aim for a clear visual hierarchy: artwork first, mat as generous breathing space, moulding as a restrained border. Many framers use the golden ratio (about 1.618) to size the bottom border relative to the sides, or simply keep the mat 1.5-2x the moulding width. Trust your eye - preview a few options and pick the one that feels balanced." },
  { q: "Should the mat colour match the frame or the artwork?", a: "Most of the time a neutral mat (soft white, cream or light grey) lets the artwork speak and never dates. Coloured or double mats can highlight an accent colour from the work for a designed look, but keep it subtle - the mat should support the image, not overwhelm it. When in doubt, choose the quieter option." },
  { q: "Do I need to frame behind glass?", a: "Not always, but in most cases, yes. Glass (or acrylic) helps protect your artwork from dust, dirt, moisture, and, in most cases, UV light, which can fade colours. Paper-based pieces such as watercolour paintings, photographs, prints or drawings are almost always framed under glass or acrylic. Canvas art or textured acrylic paintings are often framed without glass for visual depth." },
];

const SECTIONS = [
  {
    heading: "Why proportion beats formulas",
    paragraphs: [
      "Framing is a visual craft, and the most pleasing results come from proportion rather than a single 'correct' measurement. A mat that's generous relative to both the artwork and the moulding gives the eye somewhere to rest and makes even a modest print feel considered.",
      "That said, a few time-tested systems give you a reliable starting point - and Framer's Bench builds each of them in so you can compare at a glance.",
    ],
  },
  {
    heading: "Equal borders - clean and neutral",
    paragraphs: [
      "Equal borders on all four sides are calm, modern and hard to get wrong. They suit contemporary work, photography and grid arrangements where consistency across many frames matters.",
      "The one caveat: with equal borders, artwork can appear to float slightly low in the frame. If that bothers you, reach for a weighted bottom.",
    ],
  },
  {
    heading: "The weighted bottom - the gallery classic",
    paragraphs: [
      "Making the bottom border heavier than the top and sides is the signature of gallery and museum framing. A weighting factor of about 1.2-1.5 corrects the optical illusion that makes evenly-matted pieces look like they're sinking.",
      "It's a small change that reads as 'professionally framed' to almost everyone, even if they can't say why.",
    ],
    tip: "Start with a bottom border about 1.3x the sides, then trust your eye. Taller artworks often benefit from a touch more weighting.",
  },
  {
    heading: "The golden ratio - proportion from nature",
    paragraphs: [
      "The golden ratio (roughly 1.618) has been used in art and architecture for centuries because it feels naturally balanced. Applied to framing, the bottom border is sized to the side border by that ratio for an elegant, deliberate proportion.",
      "It's especially effective for fine-art prints, botanical studies and anything you want to feel timeless.",
    ],
  },
  {
    heading: "Colour, layers and restraint",
    paragraphs: [
      "Neutral mats age gracefully and keep attention on the artwork. Double and triple mats add depth and a fine accent line — a chance to echo a colour from the work without shouting.",
    ],
    tip: "The mat should support the image. If you notice the mat before the art, soften it or make the under-mat reveal smaller.",
  },
];

export default function Aesthetics() {
  return (
    <ArticleLayout
      seoTitle="Frame & Mat Aesthetics — Ratios & Proportions"
      seoDesc="The art of frame and mat proportion: how big your mat should be, gallery weighted-bottom borders, the golden ratio, and choosing mat colour for balanced, professional results."
      path="/aesthetics"
      image={IMG}
      eyebrow="Frame & Mat Aesthetics"
      title="The art of proportion"
      intro="Ratios, borders and colour - the artistic thinking behind frames that simply look right. How to size a mat, weight a border and choose proportions your eye will love."
      sections={SECTIONS}
      faqs={FAQS}
      ctaTitle="Why does framing feel harder than making the art?"
      ctaText="Preview these proportions live and let Framer's Bench handle the maths - so you can focus on how it looks, not on the arithmetic."
    />
  );
}
